import { useCallback, useEffect, useState } from 'react';
import {
  adminApi, ApiError, opsApi,
  type AdminOrderDto, type AdminStatsDto, type OpsCueDto, type OpsDockSummaryDto,
  type OpsRiskDto, type OpsTaskDto,
} from '../api';
import { setupActions, useEventReadiness, type EventReadinessState } from '../console';
import { fullyReadable, type ReadinessTab } from '../readiness';
import { ReadinessSection } from './ReadinessPanel';

/**
 * TICK-128 — one screen that answers "what needs me right now", from numbers the system owns.
 *
 * TICK-132 — and it is now the only TODAY screen: 'ภาพรวม' was retired into it. What that removed
 * from this file is the commerce half. The four tiles here were the same four figures as the
 * overview's `StatsPanel` cards, which the shell renders directly below this panel — two readings
 * of `getStats` stacked on one page, one of them poorer. So this keeps the part no other panel
 * does: the queue read against a clock, the Operations figures, and the list of what to go and do.
 *
 * `stats` arrives as a prop for the same reason: the shell already polls it for the panels below,
 * and a second fetch on the same screen could show a different number a second apart.
 *
 * TICK-135 — the readiness percentage and the phase cards the mockup puts above this panel now
 * exist, in `ReadinessSection`, which the shell renders directly above it. They are kept apart on
 * purpose and it is the same split TICK-135 rule 2 draws: readiness answers "was this configured",
 * this panel answers "who is waiting right now". One moves when a person fills a form in; the other
 * moves when a buyer uploads a slip. A single figure carrying both would flicker with traffic.
 *
 * Still missing from the mockup, and still said out loud on the page rather than only here: the
 * live timeline and the `47/min` scan rate. Both need a central event log the system does not keep,
 * and a rate needs a window nobody has chosen. Arithmetic that looks plausible would put a number
 * on an organizer's screen that no query can defend.
 *
 * Every figure here is read from an endpoint that already existed before this file: commerce from
 * `getStats` and the awaiting queue, operations from the tables TICK-121–125 gave screens to. It
 * writes nothing.
 */

const MINUTES = 60_000;
/** The queue threshold the sales inbox in the mockup uses, and the only time constant on this screen. */
const SLIP_ATTENTION_MINUTES = 15;

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

interface Loaded {
  awaiting: AdminOrderDto[];
  /** Empty for a `sales` token: the four Operations reads are not made at all. */
  tasks: OpsTaskDto[];
  risks: OpsRiskDto[];
  cues: OpsCueDto[];
  docks: OpsDockSummaryDto[];
}

const EMPTY_OPS: Omit<Loaded, 'awaiting'> = { tasks: [], risks: [], cues: [], docks: [] };

type GoTo = (tab: ReadinessTab | 'review', sub?: 'awaiting_verification' | 'problem') => void;

/**
 * TICK-148 — the readiness strip and the action list, fed by one read of the twelve checks.
 *
 * Before this the strip read them itself and the list knew nothing of them, which is how a page
 * could say "ยังไม่พร้อม" and "ไม่มีอะไรค้างอยู่ในตอนนี้" at once. Read here, both draw the same object.
 * Mounted only while the Command Center is open, so coming back after fixing something re-reads.
 */
export function CommandCenterHome({ token, eventSlug, role, stats, onGoTo }: {
  token: string;
  eventSlug?: string;
  role: string | null;
  stats: AdminStatsDto | null;
  onGoTo: GoTo;
}) {
  const readiness = useEventReadiness(token, eventSlug, role);
  return (
    <>
      <ReadinessSection state={readiness} onGoTo={onGoTo} />
      <CommandCenterPanel
        token={token}
        eventSlug={eventSlug}
        role={role}
        stats={stats}
        readiness={readiness}
        onGoTo={onGoTo}
      />
    </>
  );
}

export function CommandCenterPanel({ token, eventSlug, role, stats, readiness, onGoTo }: {
  token: string;
  eventSlug?: string;
  /**
   * A `sales` token is answered 403 by every `ops` endpoint. It sees the sales half of this screen —
   * which is the whole of what it used to see on 'ภาพรวม' — and the Operations reads are skipped
   * rather than made and swallowed, so a real permission failure still surfaces as an error.
   */
  role: string | null;
  /** Polled by the shell, which renders the detailed sales panels from the same object. */
  stats: AdminStatsDto | null;
  /** TICK-148 — the same readiness the strip above draws; failed blocking checks lead the list. */
  readiness: EventReadinessState;
  /** Every number that means "somebody has to do something" is a link to the screen where it is done. */
  onGoTo: GoTo;
}) {
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState('');
  const withOps = role !== 'sales';

  const load = useCallback(async () => {
    try {
      const awaiting = await adminApi.listOrders(token, 'awaiting_verification', undefined, eventSlug);
      if (!withOps) {
        setData({ awaiting, ...EMPTY_OPS });
        return;
      }
      const [tasks, risks, cues, docks] = await Promise.all([
        opsApi.listTasks(token, eventSlug),
        opsApi.listRisks(token, eventSlug),
        opsApi.listCues(token, eventSlug),
        opsApi.listDocks(token, eventSlug),
      ]);
      setData({ awaiting, tasks, risks, cues, docks });
    } catch (e) {
      setError(errorText(e, 'โหลดข้อมูลของหน้านี้ไม่สำเร็จ'));
    }
  }, [token, eventSlug, withOps]);

  useEffect(() => { void load(); }, [load]);

  if (!data || !stats) {
    return error ? <div className="form-error">{error}</div> : <div style={{ color: 'var(--dim)', fontSize: 13 }}>กำลังโหลด…</div>;
  }

  const { awaiting, tasks, risks, cues, docks } = data;

  /*
   * Age is measured from `submittedAt` — when the buyer handed the slip over — not from when the
   * order was created. An order that sat unpaid for an hour and was paid a minute ago has not been
   * waiting on us for an hour.
   */
  const now = Date.now();
  const waitingTooLong = awaiting.filter((o) => o.submittedAt
    && now - new Date(o.submittedAt).getTime() > SLIP_ATTENTION_MINUTES * MINUTES).length;

  const today = new Date().toISOString().slice(0, 10);
  const overdueTasks = tasks.filter((t) => t.status !== 'done' && t.slaDate < today);
  const openRisks = risks.filter((r) => r.status === 'open' || r.status === 'mitigating');
  const topRisk = [...openRisks].sort((a, b) => b.score - a.score)[0];
  const nextCue = [...cues]
    .filter((c) => c.status === 'pending' || c.status === 'ready')
    .sort((a, b) => a.time.localeCompare(b.time))[0];
  const trucksExpected = docks.filter((d) => d.status === 'scheduled').length;

  const actions: {
    id: string; label: string; detail: string;
    tab: Parameters<typeof onGoTo>[0]; sub?: Parameters<typeof onGoTo>[1];
  }[] = [];
  // TICK-148 — blocking setup checks that failed lead the list; each opens the screen that fixes it.
  for (const s of setupActions(readiness.readiness, role)) {
    actions.push({ id: s.id, label: s.label, detail: s.consequence, tab: s.tab });
  }
  if (waitingTooLong > 0) {
    actions.push({
      id: 'slips_waiting',
      label: `ตรวจสลิปที่รอเกิน ${SLIP_ATTENTION_MINUTES} นาที`,
      detail: `${waitingTooLong} รายการ`,
      tab: 'review',
      sub: 'awaiting_verification',
    });
  }
  if (stats.problemCount > 0) {
    actions.push({
      id: 'slips_problem',
      label: 'สลิปที่มีปัญหา ต้องตัดสินใจ',
      detail: `${stats.problemCount} รายการ`,
      tab: 'review',
      sub: 'problem',
    });
  }
  if (overdueTasks.length > 0) {
    actions.push({
      id: 'tasks_overdue',
      label: 'งานที่เลยกำหนดส่งแล้ว',
      detail: `${overdueTasks.length} งาน · ค้างนานสุด ${overdueTasks.map((t) => t.slaDate).sort()[0]}`,
      tab: 'opsTasks',
    });
  }
  if (topRisk) {
    actions.push({
      id: 'risks_open',
      label: 'ความเสี่ยงที่ยังเปิดอยู่',
      detail: `${openRisks.length} เรื่อง · คะแนนสูงสุด ${topRisk.score} (${topRisk.title})`,
      tab: 'opsRisks',
    });
  }

  return (
    <div className="command-center">
      {error && <div className="form-error">{error}</div>}

      <section>
        <h3 style={{ marginBottom: 4 }}>ต้องทำต่อ</h3>
        <p className="section-note">ข้อตั้งค่าที่ยังขาดมาก่อน แล้วเรียงจากสิ่งที่มีคนรออยู่ปลายทาง ไปหาสิ่งที่ทีมงานเป็นคนรอ</p>

        {actions.length === 0 && readiness.loading ? (
          /* Not the empty state: the setup checks have not been read yet, so "nothing" is not known. */
          <div className="admin-todo-clear">กำลังตรวจความพร้อมของงาน… ยังไม่สรุปว่ามีอะไรค้างหรือไม่</div>
        ) : actions.length === 0 ? (
          <>
            <div className="admin-todo-clear">ไม่มีอะไรค้างอยู่ในตอนนี้</div>
            {/* Said only when it is true of everything: a check the token could not read was not counted. */}
            {!readiness.error && fullyReadable(readiness.readiness) && (
              <p className="section-note">ข้อตั้งค่าที่ต้องมีผ่านครบทุกเฟส</p>
            )}
            {role !== 'sales' && !(!readiness.error && fullyReadable(readiness.readiness)) && (
              <p className="section-note">
                {readiness.error
                  ? 'อ่านรายการความพร้อมไม่สำเร็จ จึงยังไม่นับข้อตั้งค่าในรายการนี้'
                  : 'ข้อตั้งค่าบางข้อที่ Token นี้อ่านไม่ได้ ไม่ได้นับรวมในรายการนี้'}
              </p>
            )}
          </>
        ) : (
          <div className="command-action-list">
            {actions.map((a) => (
              <button key={a.id} type="button" className="command-action" onClick={() => onGoTo(a.tab, a.sub)}>
                <span className="lbl">{a.label}</span>
                <span className="det">{a.detail}</span>
                <span aria-hidden className="arw">→</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {withOps && (
        <section>
          <h3>การเตรียมงานและวันแสดง</h3>
          <div className="ticket-edit-grid">
            <Tile label="งานที่เลยกำหนด" value={String(overdueTasks.length)} sub={`จากงานที่ยังไม่เสร็จ ${tasks.filter((t) => t.status !== 'done').length} งาน`} />
            <Tile label="ความเสี่ยงที่เปิดอยู่" value={String(openRisks.length)} sub={topRisk ? `คะแนนสูงสุด ${topRisk.score}` : 'ไม่มีความเสี่ยงที่เปิดอยู่'} />
            <Tile label="คิวถัดไป" value={nextCue ? nextCue.time : '—'} sub={nextCue ? nextCue.title : 'ยังไม่มีคิวที่รออยู่'} />
            <Tile label="รถที่ยังไม่มาถึง" value={String(trucksExpected)} sub={`จากรอบลงของทั้งหมด ${docks.length} รอบ`} />
          </div>
        </section>
      )}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="kpi-tile">
      <div className="k-label">{label}</div>
      <div className="k-value">{value}</div>
      <div className="k-sub">{sub}</div>
    </div>
  );
}
/**
 * The screen's footnote, exported separately because it belongs at the bottom of the *page* and the
 * page is more than this panel: the shell renders the sales figures, the zones and the revenue trend
 * below it (TICK-132). Left inside the panel it would sit in the middle, reading as a footnote to
 * the Operations tiles rather than to the screen.
 */
export function CommandCenterNote({ role }: { role: string | null }) {
  return (
    <>
      {/*
        * Said on the screen rather than only in the ticket: an organizer comparing this with the
        * approved mockup will notice what is missing, and should be told why rather than left to
        * assume it is coming. TICK-135 removed two of the three items this used to list — readiness
        * and per-phase progress now have a definition and sit at the top of the page — so what is
        * left is the one thing that still has no source. A sales token is told something different,
        * because the half it cannot see is a permission boundary rather than a gap in the data.
        */}
      {role !== 'sales' ? (
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--dim)', lineHeight: 1.8 }}>
          หน้านี้แสดงเฉพาะตัวเลขที่ระบบมีที่มาจริง · ความพร้อมรายเฟสด้านบนนับจาก 17 ข้อที่นิยามไว้ใน
          TICK-135 และนับเฉพาะสิ่งที่ต้องตั้งค่าล่วงหน้า · <strong>ยังไม่มี</strong> ไทม์ไลน์สดและอัตรา
          การสแกนต่อนาที เพราะยังไม่มีบันทึกเหตุการณ์กลางให้คำนวณ — ตัวเลขที่คำนวณจากการเดาจะดูน่าเชื่อ
          กว่าที่มันเป็นจริง
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--dim)', lineHeight: 1.8 }}>
          Token ฝ่ายขายเห็นเฉพาะส่วนการขาย · งานที่เลยกำหนด ความเสี่ยง คิว และรอบลงของ
          เป็นข้อมูลของทีมปฏิบัติการ ซึ่งสิทธิ์ของ Token นี้เข้าไม่ถึง
        </p>
      )}
    </>
  );
}
